import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useState } from 'react';
import Button from '../../components/Button';
import OverflowMenu from '../../components/OverflowMenu';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import overflowMenuIcon from '../../icons/non-prefilled/16x16/overflowMenu.svg';
import { MoveStates, PostStatus, TopicStatus } from '../constants';
import type {
  PostReorganizationMetadata,
  TopicReorganizationMetadata,
} from '../useTopicReorganization';
import * as styles from './TopicTreeCardItem.module.scss';

const getTooltipForStatus = (
  type: 'topic' | 'post',
  status: TopicStatus | PostStatus,
) => {
  switch (status) {
    case TopicStatus.deleted:
    case PostStatus.deleted:
      return `This ${type}\nwill be deleted`;
    case PostStatus.archived:
      return `This ${type}\nwill be archived`;
    default:
      return null;
  }
};

type ReorganizationMetadata =
  | TopicReorganizationMetadata
  | PostReorganizationMetadata;

interface GetTooltipsArgs {
  reorganizationMetadata: ReorganizationMetadata;
  inheritedMove: ReorganizationMetadata['move'];
  type: 'topic' | 'post';
  topicOriginalName: string | undefined;
}

const getTooltips = ({
  reorganizationMetadata,
  inheritedMove,
  type,
  topicOriginalName,
}: GetTooltipsArgs) => {
  const { status } = reorganizationMetadata;

  const tooltips = [];
  if (inheritedMove.state !== MoveStates.movedOut) {
    if ('isNew' in reorganizationMetadata && reorganizationMetadata.isNew) {
      tooltips.push(`This ${type}\nwill be added`);
    } else if (
      'name' in reorganizationMetadata &&
      reorganizationMetadata.name
    ) {
      tooltips.push(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `This ${type}\nwill be renamed\nfrom “${topicOriginalName}”`,
      );
    }

    const statusTooltip = getTooltipForStatus(type, status);
    if (statusTooltip) {
      tooltips.push(statusTooltip);
    }

    if (
      'isPinned' in reorganizationMetadata &&
      typeof reorganizationMetadata.isPinned === 'boolean'
    ) {
      tooltips.push(
        `This ${type}\nwill be ${
          reorganizationMetadata.isPinned ? 'pinned' : 'unpinned'
        }`,
      );
    }
  }

  if (
    (!('isNew' in reorganizationMetadata) || !reorganizationMetadata.isNew) &&
    inheritedMove.state === MoveStates.movedIn
  ) {
    let targetName: 'topic' | 'post' | 'subtopic' = type;
    if (
      type === 'topic' &&
      reorganizationMetadata.move.state !== MoveStates.movedIn
    ) {
      targetName = 'subtopic';
    }
    tooltips.push(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `This ${targetName} will be\nmoved from ${inheritedMove.source?.name}`,
    );
  }

  return tooltips;
};

interface TopicTreeCardItemProps {
  children?: React.ReactNode;
  type: 'post' | 'topic';
  titleLine: React.ReactNode;
  controls?: React.ReactNode;
  menu: React.ReactNode;
  reorganizationMetadata:
    | TopicReorganizationMetadata
    | PostReorganizationMetadata;
  ancestorsReorganizationMetadata: TopicReorganizationMetadata[];
  originalName?: string;
  disableTooltip?: boolean;
}

const TopicTreeCardItem = ({
  children,
  type,
  titleLine,
  controls,
  menu,
  reorganizationMetadata,
  ancestorsReorganizationMetadata,
  originalName,
  disableTooltip,
}: TopicTreeCardItemProps) => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { status } = reorganizationMetadata;

  const { move: inheritedMove } = _.findLast(
    [...ancestorsReorganizationMetadata.slice(1), reorganizationMetadata],
    (metadata) => metadata.move.state !== MoveStates.initial,
  ) || {
    move: { state: MoveStates.initial },
  };

  const tooltips = getTooltips({
    reorganizationMetadata,
    inheritedMove,
    type,
    topicOriginalName: originalName,
  });

  const isNew =
    'isNew' in reorganizationMetadata && reorganizationMetadata.isNew;

  return (
    <div
      className={classNames(styles.container, styles[type], {
        [styles.movedIn]:
          !isNew &&
          inheritedMove.state === MoveStates.movedIn &&
          (type === 'post' || ancestorsReorganizationMetadata.length > 0),
      })}
    >
      <div
        onMouseEnter={() => setIsMenuVisible(true)}
        onMouseLeave={() => setIsMenuVisible(false)}
      >
        <div
          className={classNames(styles.titleLine, {
            ...(inheritedMove.state !== MoveStates.movedOut
              ? {
                  [styles.created]: isNew,
                  [styles[status]]: !isNew,
                  [styles.renamed]:
                    !isNew &&
                    'name' in reorganizationMetadata &&
                    reorganizationMetadata.name,
                }
              : {}),
          })}
        >
          <div className={styles.title}>
            {!disableTooltip && tooltips.length > 0 ? (
              <Tooltip content={tooltips.join('\n\n')} display="block">
                {titleLine}
              </Tooltip>
            ) : (
              <>{titleLine}</>
            )}
            {menu ? (
              <div
                className={classNames(styles.menu, {
                  [styles.visible]: isMenuVisible,
                  [styles.open]: isMenuOpen,
                })}
              >
                <OverflowMenu
                  open={isMenuOpen}
                  onOpenChange={setIsMenuOpen}
                  inline={false}
                  button={
                    <Button className={styles.button}>
                      <SVGIcon size={14}>{overflowMenuIcon}</SVGIcon>
                    </Button>
                  }
                >
                  {menu}
                </OverflowMenu>
              </div>
            ) : null}
          </div>
          {!!controls && <div className={styles.controls}>{controls}</div>}
        </div>
      </div>
      {children}
    </div>
  );
};

export default TopicTreeCardItem;
